import { useState, useRef, useCallback, useEffect } from 'react'
import { EditorView } from 'prosemirror-view'

type UsePopoverPositionParams = {
  getEditorView: () => EditorView | null
  anchorPos: number | null
}

/**
 * Converts a ProseMirror document position into screen coordinates (DOMRect)
 * and keeps it updated as the user scrolls or the window resizes.
 * Hides the rect when the anchor scrolls out of the visible container.
 */
export function usePopoverPosition({ getEditorView, anchorPos }: UsePopoverPositionParams) {
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const updateRect = useCallback(() => {
    const view = getEditorView()
    const container = scrollContainerRef.current
    if (!view || !container || anchorPos === null) {
      setPopoverRect(null)
      return
    }
    if (anchorPos < 0 || anchorPos > view.state.doc.content.size) {
      setPopoverRect(null)
      return
    }
    const coords = view.coordsAtPos(anchorPos)
    const containerRect = container.getBoundingClientRect()
    if (coords.bottom < containerRect.top || coords.top > containerRect.bottom) {
      setPopoverRect(null)
    } else {
      // Align horizontally with the left edge of the editor content area
      const editorLeft = view.dom.getBoundingClientRect().left
      setPopoverRect(new DOMRect(editorLeft, coords.top, 0, coords.bottom - coords.top))
    }
  }, [getEditorView, anchorPos])

  // Compute rect when anchorPos changes
  useEffect(() => {
    if (anchorPos === null) {
      setPopoverRect(null)
      return
    }
    updateRect()
  }, [anchorPos, updateRect])

  // Register scroll/resize listeners while the popover is visible
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el || !popoverRect) return
    el.addEventListener('scroll', updateRect)
    window.addEventListener('resize', updateRect)
    return () => {
      el.removeEventListener('scroll', updateRect)
      window.removeEventListener('resize', updateRect)
    }
  }, [popoverRect, updateRect])

  return { popoverRect, scrollContainerRef }
}
